"use client"

import { discardCartOnRegionChange } from "@/lib/data/cart"
import { HttpTypes } from "@medusajs/types"
import { useParams, usePathname, useRouter } from "next/navigation"
import { useMemo, useState } from "react"
import ReactCountryFlag from "react-country-flag"

type CountryOption = {
  country: string
  region: string
  label: string
}

export const BarebonesCountrySelector = ({
  regions,
}: {
  regions: HttpTypes.StoreRegion[]
}) => {
  const router = useRouter()
  const params = useParams()
  const pathname = usePathname()
  const [open, setOpen] = useState(false)
  const [switching, setSwitching] = useState(false)

  const countryCode = String(
    Array.isArray(params.locale) ? params.locale[0] : params.locale || ""
  ).toLowerCase()

  const currentPath = pathname.split(`/${countryCode}`)[1] || ""

  const options = useMemo(() => {
    return regions
      ?.map((r) =>
        r.countries?.map((c) => ({
          country: (c.iso_2 || "").toLowerCase(),
          region: r.id,
          label: c.display_name || "",
        }))
      )
      .flat()
      .filter((o): o is CountryOption => !!o?.country)
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [regions])

  const current = options?.find((o) => o.country === countryCode) ?? null

  const hrefFor = (country: string) => `/${country}${currentPath}`

  const handleSelect = (option: CountryOption) => {
    if (!option.country || option.country === countryCode || switching) return

    setSwitching(true)
    setOpen(false)
    router.prefetch(hrefFor(option.country))

    if (current?.region && option.region !== current.region) {
      void discardCartOnRegionChange()
    }

    window.location.assign(hrefFor(option.country))
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        disabled={switching}
        aria-label="Select region"
        aria-expanded={open}
        aria-haspopup="menu"
        className="bb-nav-trigger inline-flex h-8 w-8 items-center justify-center text-[#2b2118] transition-opacity hover:opacity-70 disabled:opacity-50"
      >
        {current ? (
          // @ts-expect-error react-country-flag types
          <ReactCountryFlag
            svg
            countryCode={current.country}
            style={{ width: "16px", height: "16px" }}
          />
        ) : (
          <span className="text-[11px] uppercase tracking-[0.14em]">
            {countryCode || "—"}
          </span>
        )}
      </button>

      <div
        className={`bb-dropdown bb-dropdown--end absolute right-0 top-full z-50 pt-3 ${
          open ? "bb-dropdown--open" : ""
        }`}
        aria-hidden={!open}
      >
        <div
          role="menu"
          className="bb-dropdown-panel no-scrollbar max-h-60 min-w-[200px] overflow-auto"
        >
          <p className="bb-dropdown-heading">Region</p>
          {options?.map((option) => {
            const active = option.country === countryCode
            return (
              <button
                key={option.country}
                type="button"
                role="menuitem"
                disabled={switching}
                tabIndex={open ? 0 : -1}
                onMouseEnter={() => router.prefetch(hrefFor(option.country))}
                onFocus={() => router.prefetch(hrefFor(option.country))}
                onClick={() => handleSelect(option)}
                className={`bb-dropdown-item bb-dropdown-item--region ${
                  active ? "bb-dropdown-item--active" : ""
                }`}
              >
                <span className="bb-region-option">
                  {/* @ts-expect-error react-country-flag types */}
                  <ReactCountryFlag
                    svg
                    countryCode={option.country}
                    className="bb-region-flag"
                    style={{ width: "16px", height: "16px" }}
                  />
                  <span className="bb-region-label">{option.label}</span>
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
